import { contentUtils } from '../content';
import {
  LATEST_PARAMETER_RESOLVER_SCHEMA_VERSION as LATEST_SCHEMA_VERSION,
  ParameterResolver,
  ModelParameterResolver as Parameter,
} from '../../../emissionsModel/parameterResolverSchema';
import { findByName } from '../../../emissionsModel/utils';
import assertNever from '@watershed/shared-util/assertNever';
import { UnexpectedError } from '@watershed/errors/UnexpectedError';

/**
 * Applies a customized parameter to its underlying default parameter. Depending
 * on the customization mode, the customized parameter's expression is either
 * replaced or coalesced with the default parameter's expression.
 */
function applyCustomizationToParameter(
  orig: Parameter,
  custom?: contentUtils.CustomizationParameter
): Parameter {
  if (!custom) return orig;

  if (custom.mode === 'replace') {
    return { name: orig.name, expression: custom.expression };
  }
  if (custom.mode === 'coalesce') {
    return {
      name: orig.name,
      expression: `coalesce(${custom.expression}, ${orig.expression})`,
    };
  }
  assertNever(custom.mode);
}

/**
 * Performs the simple zipping of custom parameters onto the parameter resolver.
 *
 * For custom parameters of mode = 'coalesce', they are wrapped in an lscript
 * expression that coalesces with the base parameter resolver's value.
 *
 * For custom parameters of mode = 'replace', they replace the base parameter
 * resolver's value entirely.
 */
export function coalesceCustomizationOnParameterResolver(
  { parameters: customParameters }: contentUtils.CustomizationContent,
  { schemaVersion: baseSchemaVersion, modelParameters }: ParameterResolver
): ParameterResolver {
  UnexpectedError.invariant(
    baseSchemaVersion === LATEST_SCHEMA_VERSION,
    'Customization coalesce should only happen on the latest parameter resolver schema version. ' +
      'Ensure caller is using autoupdate utilities.',
    { data: { baseSchemaVersion } }
  );

  const newParameters = modelParameters.map((orig) => {
    const customParam = findByName(customParameters, orig.name);
    return applyCustomizationToParameter(orig, customParam);
  });

  return {
    schemaVersion: LATEST_SCHEMA_VERSION,
    modelParameters: newParameters,
  };
}
